// src/portal/PortalPlanReviewsPage.jsx
//
// The list. One route (5), which returns the counts and the rows together so
// the card above the list and the list cannot disagree — the server builds both
// from one predicate for exactly that reason.
//
// ── THE SAME PAGE SERVES BOTH ROLES, AND THE SERVER DECIDES WHICH ───────────
//
// `scope` comes back 'submitter' or 'org'. The client NEVER re-derives it:
// listScopeFor branches on the caller's licence, and a client that guessed
// would either show a submitter someone else's plans or hide a reviewer's own
// queue. The only thing this page does with `scope` is choose a heading.
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { listPlanReviews, createPlanReview, listGrants, PortalError } from "../api/portalApi";
import { useAuthStore } from "../stores/useAuthStore";
import { rowCounterparty } from "./portalCounterparty";

const STATUS_LABEL = {
  draft: "Draft",
  submitted: "With the reviewer",
  changes_requested: "Changes requested",
  approved: "Approved",
};

// The submitter's reading and the reviewer's reading of the same status are not
// the same sentence. "With the reviewer" is what a submitter needs to know;
// "Waiting on you" is what a reviewer does.
const STATUS_LABEL_REVIEWER = {
  ...STATUS_LABEL,
  submitted: "Waiting on you",
  changes_requested: "Back with the submitter",
};

export default function PortalPlanReviewsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState("");
  const [creating, setCreating] = useState(false);
  // null while unknown, [] for an org nobody has named, rows otherwise. The
  // three states are distinct and the middle one is the common case.
  const [grants, setGrants] = useState(null);
  const [grantsFailed, setGrantsFailed] = useState(false);
  const [owner, setOwner] = useState("");

  // ── THE CALLER'S OWN ORG, FOR THE "my organisation" OPTION ───────────────
  //
  // profile.org_id is /auth/me's `org_id`, which auth.mjs:57 documents as the
  // RESOLVED context -- req.orgId from resolveOrgContext, the same resolution
  // route 1 performs on the create. So picking this option sends the id route 1
  // would have defaulted to, rather than a second opinion about which org the
  // caller is in.
  const profile = useAuthStore((s) => s.profile);
  const profileLoaded = useAuthStore((s) => s.profileLoaded);
  const loadProfile = useAuthStore((s) => s.loadProfile);
  const myOrgId = profile?.org_id ?? null;

  async function load() {
    try {
      setData(await listPlanReviews());
      setError(null);
    } catch (e) {
      setError(e instanceof PortalError ? e : new PortalError(0, { error: String(e) }));
    }
  }
  useEffect(() => { load(); }, []);

  const isSubmitter = data?.scope === "submitter";
  const labels = isSubmitter ? STATUS_LABEL : STATUS_LABEL_REVIEWER;

  // The awaiting-you derivation moved to PortalLauncherPage with the card that
  // used it. It is still route 5's `awaiting_statuses` and still not restated
  // on the client -- it just has one consumer now instead of two.

  // ── GRANTS, ONLY FOR THE SIDE THAT CREATES ───────────────────────────────
  //
  // A reviewer has no create form, so there is no picker to populate and the
  // call is not made. Gating on `isSubmitter` rather than fetching
  // unconditionally also keeps the portal's network footprint equal to what the
  // page actually uses, which PortalShell.fetches.test.jsx measures.
  useEffect(() => {
    if (!isSubmitter) return undefined;
    let alive = true;
    (async () => {
      try {
        const res = await listGrants();
        if (alive) { setGrants(res?.grants ?? []); setGrantsFailed(false); }
      } catch {
        // NOT SWALLOWED INTO []. An empty list means "nobody has named you",
        // which hides the picker and sends the create with no owner -- the
        // exact silent fallback that produced LP-2026-0001. A failure is a
        // different fact and the form refuses to guess on it.
        if (alive) { setGrants(null); setGrantsFailed(true); }
      }
    })();
    return () => { alive = false; };
  }, [isSubmitter]);

  // The profile is loaded by PortalShell, but this page is mounted by a router
  // and a page that DEPENDS on another component having fetched something is a
  // page that breaks when the layout changes. loadProfile debounces on
  // profileLoading, so asking again costs nothing.
  useEffect(() => {
    if (isSubmitter && !profileLoaded) loadProfile();
  }, [isSubmitter, profileLoaded, loadProfile]);

  // ── WHEN IS THERE A CHOICE TO MAKE? ──────────────────────────────────────
  //
  // Only when some owner has named this org. Every org on this platform but one
  // has no grants, and for those the create form is what it has always been: a
  // title, and the review is theirs. A picker listing only themselves would
  // present a decision that has one answer.
  //
  // THE SAME PREDICATE ROUTE 1 REFUSES ON -- any grant, live or revoked. If
  // this said `.some(g => g.live)` an operator holding only a revoked grant
  // would get no picker and a server that demands the field it gives them no
  // way to send.
  const hasGrants = Array.isArray(grants) && grants.length > 0;
  const liveGrants = hasGrants ? grants.filter((g) => g.live) : [];
  // With a picker, the owner is explicit or the form does not submit. Without
  // one, it is absent and route 1's default -- the caller's own org -- stands.
  const ownerReady = hasGrants ? owner !== "" : true;

  async function onCreate(e) {
    e.preventDefault();
    if (!title.trim() || creating || !ownerReady) return;
    setCreating(true);
    try {
      // "self" is not sent. It means "no owner named", which is precisely what
      // route 1's default resolves, and it is only offerable when myOrgId is
      // known -- see the option below.
      await createPlanReview({
        title: title.trim(),
        ownerOrgId: hasGrants && owner !== "self" ? owner : null,
      });
      setTitle("");
      setOwner("");
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setCreating(false);
    }
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-fg-secondary">
        <p className="text-red-400">{error.message}</p>
        {/* THE CODE IS SHOWN. A submitter reporting a problem to their contact
            at the customer can quote it, and it is the difference between "the
            portal is broken" and "license_assignment_missing". */}
        {error.code && <p className="mt-1 font-mono text-xs opacity-70">{error.code}</p>}
      </div>
    );
  }

  if (!data) return <div className="p-6 text-sm text-fg-secondary">Loading…</div>;

  return (
    <div className="op-portal-surface flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-6">
      <h1 className="font-display text-base font-semibold text-fg-primary">
        {isSubmitter ? "Your plan reviews" : "Plan reviews"}
      </h1>

      {/* THE ROLLUP CARD IS NOT HERE ANY MORE. It moved to the launcher at
          /portal, which is what an index is for; this page is the list.

          ONE CONSEQUENCE, AND IT IS NOT DECORATION: a confined submitter never
          reaches the launcher, so they no longer see a rollup at all. Their
          home is this page. If that figure is wanted for them it belongs back
          here behind `isSubmitter`, which is one line -- it is called out
          rather than quietly decided. */}

      {/* Only a submitter creates. A reviewer has nothing to upload. */}
      {isSubmitter && (
        <form onSubmit={onCreate} className="flex flex-col gap-2">
          <div className="flex gap-2">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Name this submission — e.g. Section 14 pad and access road"
              maxLength={200}
              className="min-w-0 flex-1 rounded border border-bg-border bg-bg-soft px-3 py-1.5 text-sm"
            />

            {/* ── THE PICKER, AND THE ONLY SOURCE IT MAY HAVE ──────────────
                
                Options come from route 4b and from nowhere else. That route
                filters `operator_org_id = caller`, so this lists the owners who
                named THIS org and cannot list an org the caller has not been
                disclosed. A picker fed from any other source -- an org list, a
                search, the reviewer's own grants -- would be the directory the
                route deliberately is not, rebuilt on the client. */}
            {hasGrants && (
              <select
                aria-label="Who is this plan review for?"
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                className="min-w-0 rounded border border-bg-border bg-bg-soft px-3 py-1.5 text-sm"
              >
                {/* NO PRESELECTION. A default selection is a choice made on the
                    submitter's behalf, which is the defect this commit exists
                    to remove -- moved from the server to the dropdown. */}
                <option value="">Who is this for?…</option>
                {liveGrants.map((g) => (
                  <option key={g.id} value={g.owner_org_id}>{g.owner_org_name}</option>
                ))}
                {/* An org that both receives grants and files its own plans is
                    allowed; only Landmark is a pure operator today. Offered
                    ONLY when the resolved org id is known, because the option
                    must send that id rather than rely on a server default the
                    server no longer applies to this caller. */}
                {myOrgId && <option value="self">My organization</option>}
              </select>
            )}

            <button
              type="submit"
              disabled={!title.trim() || creating || !ownerReady || grantsFailed}
              className="rounded border border-bg-border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              {creating ? "Creating…" : "New plan review"}
            </button>
          </div>

          {/* A REVOKED GRANT IS SAID, NOT HIDDEN. Route 4b returns it marked
              for this reason: "they no longer accept submissions from you" and
              "they never did" are different facts, and a missing row asserts
              the second about a case that is the first. */}
          {hasGrants && liveGrants.length < grants.length && (
            <p className="text-xs text-fg-secondary">
              {grants.filter((g) => !g.live).map((g) => g.owner_org_name).join(", ")}
              {" "}no longer accepts submissions from your organization.
            </p>
          )}

          {grantsFailed && (
            <p className="text-xs text-red-400">
              We could not load the organizations you can submit to, so we cannot
              tell where this would go. Reload the page.
            </p>
          )}
        </form>
      )}

      {data.reviews.length === 0 ? (
        <p className="text-sm text-fg-secondary">
          {isSubmitter
            ? "Nothing submitted yet. Name a submission above to start one."
            : "No plan reviews have been submitted to this organization yet."}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {data.reviews.map((r) => (
            <li key={r.id}>
              <Link
                to="/portal/plan-reviews/$planReviewId"
                params={{ planReviewId: r.id }}
                className="flex items-baseline gap-3 rounded border border-bg-border px-3 py-2 hover:border-fg-secondary"
              >
                {/* THE CASE NUMBER, which is what a submitter quotes on the
                    phone and what a reviewer files under. Monospaced so a
                    column of them lines up and a wrong year is visible. */}
                <span className="shrink-0 font-mono text-xs text-fg-secondary">
                  {r.case_number}
                </span>
                <span className="flex-1 truncate text-sm">{r.title}</span>
                {/* THE OTHER SIDE, WHEN THERE IS ONE. Null for an intra-org
                    review, which is nearly every row -- see
                    portalCounterparty.js for why a repeated name is worse than
                    no name. */}
                {rowCounterparty(r, isSubmitter) && (
                  <span className="truncate text-xs text-fg-secondary">
                    {rowCounterparty(r, isSubmitter)}
                  </span>
                )}
                {/* WHOSE, on the reviewer's list only. A submitter's list is
                    their own by construction, so a column repeating their name
                    on every row says nothing. */}
                {!isSubmitter && (
                  <span className="truncate text-xs text-fg-secondary">
                    {r.submitter_username_at_create}
                  </span>
                )}
                <span className="text-xs text-fg-secondary">
                  {r.revision_count > 0 ? `rev ${r.current_revision}` : "no submission"}
                </span>
                <span className="text-xs">{labels[r.status] ?? r.status}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
